import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/auth.api'
import { userApi } from '@/api/user.api'
import type { User } from '@/types/models'
import { UserRole } from '@/types/enums'
import i18n from '@/i18n'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(localStorage.getItem('auth_token'))
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === UserRole.ADMIN)
  const userName = computed(() => user.value?.name || 'User')
  const userEmail = computed(() => user.value?.email || '')

  // Actions
  function syncLocale(language: User['language']) {
    i18n.global.locale.value = language
    localStorage.setItem('locale', language)
    document.documentElement.lang = language
  }

  async function login(dni: string, documentType: string = 'NIF') {
    loading.value = true
    error.value = null
    try {
      const response = await authApi.login({ dni, documentType })
      // Backend returns { data: { token, user } }
      const { token: authToken, user: userData } = response.data.data
      token.value = authToken
      user.value = userData
      syncLocale(userData.language)
      localStorage.setItem('auth_token', authToken)
      return response.data.data
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || err.response?.data?.message || 'Login failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function loginWithCallback(code: string, state: string) {
    localStorage.removeItem('valid_oauth_state')
    loading.value = true
    error.value = null
    try {
      const response = await authApi.validCallback({ code, state })
      const { token: authToken, user: userData } = response.data.data
      token.value = authToken
      user.value = userData
      syncLocale(userData.language)
      localStorage.setItem('auth_token', authToken)
      return response.data.data
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || err.response?.data?.message || 'Authentication failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function initValidAuth(): Promise<string> {
    loading.value = true
    error.value = null
    try {
      const response = await authApi.validInit()
      const { authorization_url, state } = response.data.data
      localStorage.setItem('valid_oauth_state', state)
      return authorization_url
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to initialize authentication'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      await authApi.logout()
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      token.value = null
      user.value = null
      localStorage.removeItem('auth_token')


    }
  }

  async function refreshUser() {
    if (!token.value) return
    try {
      const response = await userApi.getProfile()
      // Backend returns { data: { user object } } - the user object is directly in data
      user.value = response.data.data
      syncLocale(response.data.data.language)
    } catch (err) {
      console.error('Failed to refresh user:', err)
      // If refresh fails, logout
      await logout()
      throw err
    }
  }

  function clearError() {
    error.value = null
  }

  async function updateLanguage(language: User['language']) {
    await userApi.updateLanguage({ language })
    if (user.value) {
      user.value = { ...user.value, language }
    }
    syncLocale(language)
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    // Getters
    isAuthenticated,
    isAdmin,
    userName,
    userEmail,
    // Actions
    login,
    loginWithCallback,
    initValidAuth,
    logout,
    refreshUser,
    updateLanguage,
    clearError,
  }
})
